// src/views/enseignant/ENFaireAppel.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEnseignantService } from "../../services/enseignantService";
import { useEnseignantDrawer } from "../../context/EnseignantDrawerContext";
import Swal from 'sweetalert2';

export default function ENFaireAppel({ slug }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getElevesParClasse, enregistrerPresences, getParametres, getMesClasses } = useEnseignantService();
  const { openDrawer } = useEnseignantDrawer();

  const [classes, setClasses] = useState([]);
  const [classeId, setClasseId] = useState('');
  const [currentClasse, setCurrentClasse] = useState(null);
  const [eleves, setEleves] = useState([]);
  const [selectedEleves, setSelectedEleves] = useState([]);
  const [selectionOrder, setSelectionOrder] = useState([]);
  const [typePointage, setTypePointage] = useState('entree');
  const [datePointage, setDatePointage] = useState(new Date().toISOString().split('T')[0]);
  const [heurePointage, setHeurePointage] = useState(new Date().toTimeString().slice(0, 8));
  const [motifSortie, setMotifSortie] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMotifModal, setShowMotifModal] = useState(false);
  const [search, setSearch] = useState("");
  const [parametres, setParametres] = useState(null);
  const [loadingParametres, setLoadingParametres] = useState(true);
  const [parametresLoaded, setParametresLoaded] = useState(false);

  const hasFetchedRef = useRef(false);
  const hasScannedForCurrentClassRef = useRef(false);

  // Extraire `classe` du searchParams une seule fois (stable)
  const classeParamFromUrl = searchParams.get('classe');

  const checkMotifRequired = () => {
    if (typePointage === 'sortie') {
      if (!parametresLoaded || !parametres) {
        return true;
      }

      try {
        const heurePointageObj = new Date(`2000-01-01T${heurePointage}`);
        const pauseDebut = new Date(`2000-01-01T${parametres.pause_debut || "12:00:00"}`);
        const pauseFin = new Date(`2000-01-01T${parametres.pause_fin || "14:00:00"}`);
        const heureSortieMin = new Date(`2000-01-01T${parametres.heure_sortie_min || "17:00:00"}`);

        const isPause = heurePointageObj >= pauseDebut && heurePointageObj <= pauseFin;
        const isDescente = heurePointageObj >= heureSortieMin;

        return !isPause && !isDescente;
      } catch (error) {
        return true;
      }
    }
    return false;
  };

  // ─── CHARGEMENT DES CLASSES + PARAMÈTRES ─────────────────────────────
  const loadInitialData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant établissement manquant.");
      setLoadingClasses(false);
      return;
    }

    setLoadingClasses(true);
    setError(null);

    try {
      setLoadingParametres(true);

      const classesData = await getMesClasses(slug);
      setClasses(classesData || []);

      if (classeParamFromUrl && classesData.some(c => String(c.id) === classeParamFromUrl)) {
        setClasseId(classeParamFromUrl);
        setCurrentClasse(classesData.find(c => String(c.id) === classeParamFromUrl));
      } else if (classesData.length > 0) {
        setClasseId(classesData[0].id);
        setCurrentClasse(classesData[0]);
      }

      const paramsData = await getParametres(slug);
      if (paramsData && Object.keys(paramsData).length > 0) {
        setParametres(Array.isArray(paramsData) ? paramsData[0] : paramsData);
      } else {
        setParametres({
          pause_debut: "12:30:00",
          pause_fin: "14:00:00",
          heure_sortie_min: "16:00:00"
        });
      }
      setParametresLoaded(true);
    } catch (err) {
      setError("Impossible de charger les classes et paramètres.");

      // Paramètres par défaut pour ne pas bloquer l'UI
      setParametres({
        pause_debut: "12:30:00",
        pause_fin: "14:00:00",
        heure_sortie_min: "16:00:00"
      });
      setParametresLoaded(true);
    } finally {
      setLoadingParametres(false);
      setLoadingClasses(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, classeParamFromUrl]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadInitialData();
  }, [loadInitialData]);

  // ─── CHARGEMENT DES ÉLÈVES PAR CLASSE ────────────────────────────────
  const fetchEleves = useCallback(async () => {
    if (!slug || !classeId) return;

    setLoading(true);
    try {
      const data = await getElevesParClasse(slug, classeId);
      setEleves(data || []);
      setSelectedEleves([]);
      setSelectionOrder([]);
    } catch (err) {
      Swal.fire({
        title: 'Erreur',
        text: 'Impossible de charger les élèves',
        icon: 'error',
        confirmButtonColor: '#d9534f'
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, classeId]);

  useEffect(() => {
    if (classeId) {
      fetchEleves();
    }
  }, [classeId, fetchEleves]);

  // ─── SÉLECTION ───────────────────────────────────────────────────────
  const toggleEleveSelection = (matricule) => {
    setSelectedEleves(prev => {
      if (prev.includes(matricule)) {
        setSelectionOrder(order => order.filter(m => m !== matricule));
        return prev.filter(m => m !== matricule);
      } else {
        setSelectionOrder(order => [...order, matricule]);
        return [...prev, matricule];
      }
    });
  };

  const toggleSelectAll = () => {
    const elevesFiltres = getFilteredEleves();
    if (selectedEleves.length === elevesFiltres.length) {
      setSelectedEleves([]);
      setSelectionOrder([]);
    } else {
      const allMatricules = elevesFiltres.map(eleve => eleve.matricule);
      setSelectedEleves(allMatricules);
      setSelectionOrder(allMatricules);
    }
  };

  const getFilteredEleves = () => {
    return eleves.filter(eleve =>
      `${eleve.prenom} ${eleve.nom} ${eleve.matricule}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  };

  const handleClasseChange = (id) => {
    const classe = classes.find(c => c.id === id);
    setClasseId(id);
    setCurrentClasse(classe);
    setSearch("");
    setSelectedEleves([]);
    setSelectionOrder([]);
    hasScannedForCurrentClassRef.current = false;
  };

  // ─── SOUMISSION ──────────────────────────────────────────────────────
  // `skipConfirm` : évite la double confirmation quand on vient du modal motif
  const handleSubmitPointage = async (skipConfirm = false) => {
    if (saving) return;

    if (selectedEleves.length === 0) {
      Swal.fire({
        title: 'Aucun élève sélectionné',
        text: 'Veuillez sélectionner au moins un élève',
        icon: 'warning',
        confirmButtonColor: '#1B4F80'
      });
      return;
    }

    const needsMotif = checkMotifRequired();

    // Si motif requis et vide → ouvrir le modal (sauf si déjà validé)
    if (needsMotif && !motifSortie.trim() && !skipConfirm) {
      setShowMotifModal(true);
      return;
    }

    // Si motif requis et toujours vide après passage par le modal
    if (needsMotif && !motifSortie.trim()) {
      Swal.fire({
        title: 'Motif requis',
        text: 'Veuillez saisir un motif pour la sortie',
        icon: 'warning',
        confirmButtonColor: '#1B4F80'
      });
      return;
    }

    // Swal de confirmation (sauf si skipConfirm)
    if (!skipConfirm) {
      const result = await Swal.fire({
        title: 'Confirmer le pointage',
        html: `
          <div style="text-align: left;">
            <p><strong>Type:</strong> ${typePointage === 'entree' ? 'Entrée' : 'Sortie'}</p>
            <p><strong>Date:</strong> ${datePointage}</p>
            <p><strong>Heure:</strong> ${heurePointage}</p>
            <p><strong>Nombre d'élèves:</strong> ${selectedEleves.length}</p>
            ${needsMotif && motifSortie ? `<p><strong>Motif:</strong> ${motifSortie}</p>` : ''}
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Confirmer',
        cancelButtonText: 'Annuler',
        didOpen: () => setSaving(true)
      });

      if (!result.isConfirmed) {
        setSaving(false);
        return;
      }
    } else {
      setSaving(true);
    }

    try {
      const pointagesData = selectedEleves.map(matricule => ({
        matricule: matricule,
        date: datePointage,
        heure: heurePointage,
        type_pointage: typePointage,
        motif_sortie: needsMotif ? motifSortie : null
      }));

      const response = await enregistrerPresences(slug, {
        presences: pointagesData,
        classe: parseInt(classeId)
      });

      setSaving(false);

      await Swal.fire({
        title: 'Pointage réussi !',
        html: `
          <div style="text-align: left;">
            <p><strong>Pointages réussis:</strong> ${response.created || pointagesData.length}</p>
          </div>
        `,
        icon: 'success',
        confirmButtonColor: '#28a745'
      });

      setSelectedEleves([]);
      setSelectionOrder([]);
      setMotifSortie('');
      setShowMotifModal(false);
      await fetchEleves();
    } catch (error) {
      let errorMessage = 'Erreur lors du pointage';
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          errorMessage = Object.values(error.response.data).flat().join(', ');
        } else {
          errorMessage = error.response.data;
        }
      }
      Swal.fire({
        title: 'Erreur',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#d9534f'
      });
    } finally {
      setSaving(false);
    }
  };

  // Valider le motif puis enchaîner DIRECTEMENT sur l'envoi (sans repasser
  // par le Swal de confirmation, qui a déjà été validé implicitement).
  const validerMotif = () => {
    if (!motifSortie.trim()) {
      Swal.fire({
        title: 'Motif requis',
        text: 'Veuillez saisir un motif pour la sortie',
        icon: 'warning',
        confirmButtonColor: '#1B4F80'
      });
      return;
    }
    setShowMotifModal(false);
    handleSubmitPointage(true); // ⬅️ skipConfirm = true
  };

  const elevesFiltres = getFilteredEleves();
  const allSelected = elevesFiltres.length > 0 && selectedEleves.length === elevesFiltres.length;
  const isPointageButtonDisabled = saving || selectedEleves.length === 0 || loadingParametres;

  // ─── HEADER ────────────────────────────────────────────────────────
  const Header = () => (
    <div
      style={{
        width: "100%",
        padding: "52px 16px 40px",
        background: "linear-gradient(135deg, var(--pr-dk), var(--pr))",
        borderRadius: "0 0 40px 40px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.22)",
        position: "relative",
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      <div style={{
        position: "absolute",
        left: "20%",
        right: "20%",
        bottom: 0,
        height: 3,
        background: "linear-gradient(90deg, transparent, var(--or), transparent)",
      }} />

      <button
        onClick={openDrawer}
        style={{
          position: "absolute",
          top: 44,
          left: 16,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: 12,
          width: 42,
          height: 42,
          cursor: "pointer",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ti ti-menu-2" style={{ color: "#fff", fontSize: 22 }} />
      </button>

      <button
        onClick={() => navigate(`/${slug}/enseignant/accueil`)}
        style={{
          position: "absolute",
          top: 44,
          right: 16,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: 12,
          width: 42,
          height: 42,
          cursor: "pointer",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ti ti-home" style={{ color: "#fff", fontSize: 20 }} />
      </button>

      <div style={{ marginTop: 20 }}>
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 10,
          }}
        >
          <i className="ti ti-user-check" style={{ fontSize: 30, color: "#fff" }} />
        </div>
        <h1
          style={{
            fontFamily: "Poppins, sans-serif",
            color: "#fff",
            fontSize: 22,
            fontWeight: "bold",
            textShadow: "0 2px 6px rgba(0,0,0,0.3)",
          }}
        >
          Faire l'appel
        </h1>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            color: "rgba(255,255,255,0.75)",
            fontSize: 13,
            marginTop: 6,
          }}
        >
          Prenez rapidement la présence de vos élèves en classe
        </p>
      </div>
    </div>
  );

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }

          /* ─── Loader intégré dans la zone de contenu ─── */
          .en-faire-appel-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 80px 20px;
            gap: 14px;
          }
          .en-faire-appel-loading .spinner {
            width: 44px;
            height: 44px;
            border: 4px solid var(--pr-bg, #e5eef7);
            border-top-color: var(--pr);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          .en-faire-appel-loading span {
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            color: var(--m);
          }

          /* ─── Carte d'erreur intégrée ─── */
          .en-faire-appel-error {
            background: #fff;
            border-radius: 20px;
            padding: 40px 20px;
            text-align: center;
            box-shadow: 0 4px 18px rgba(0,0,0,0.08);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
            margin: 16px;
          }
          .en-faire-appel-error i.alert-icon {
            font-size: 48px;
            color: var(--rd);
          }
          .en-faire-appel-error h2 {
            font-family: 'Poppins', sans-serif;
            font-size: 17px;
            font-weight: 600;
            color: var(--t);
            margin: 4px 0 0;
          }
          .en-faire-appel-error p {
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            color: var(--m);
            margin: 0 0 12px;
          }
          .en-faire-appel-error .actions-row {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: center;
          }
          .en-faire-appel-error .btn {
            border: none;
            border-radius: 30px;
            padding: 10px 22px;
            cursor: pointer;
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            font-weight: 600;
            color: #fff;
            background: var(--pr);
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: opacity 0.2s;
          }
          .en-faire-appel-error .btn:hover { opacity: 0.9; }
          .en-faire-appel-error .btn.btn-secondary {
            background: var(--b2);
            color: var(--t);
          }
        `}
      </style>

      <div
        style={{
          background: "var(--s2)",
          minHeight: "100vh",
        }}
      >
        {/* Header TOUJOURS affiché */}
        <Header />

        {loadingClasses ? (
          /* ── ÉTAT CHARGEMENT ── */
          <div className="en-faire-appel-loading">
            <div className="spinner"></div>
            <span>Chargement des classes…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="en-faire-appel-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button
                className="btn"
                onClick={() => {
                  hasFetchedRef.current = false;
                  loadInitialData();
                }}
              >
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/enseignant/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <div style={{ padding: 16, paddingBottom: 40 }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: 20,
                boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                marginBottom: 20,
                animation: "fadeUp 0.4s ease both",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 15 }}>
                <i className="ti ti-school" style={{ color: "var(--pr)", fontSize: 20 }} />
                <h3 style={{ margin: 0, color: "var(--pr)", fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: "bold" }}>
                  Sélectionnez une classe
                </h3>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  overflowX: "auto",
                  gap: 10,
                  paddingBottom: 8,
                  scrollbarWidth: "thin",
                }}
              >
                {classes.map(classe => (
                  <button
                    key={classe.id}
                    onClick={() => handleClasseChange(classe.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 16px",
                      borderRadius: 30,
                      cursor: "pointer",
                      border: `1.5px solid ${classeId === classe.id ? "var(--pr)" : "#e5e7eb"}`,
                      background: classeId === classe.id ? "var(--pr)" : "#fff",
                      color: classeId === classe.id ? "#fff" : "var(--pr)",
                      fontWeight: 600,
                      fontSize: 13,
                      fontFamily: "Inter, sans-serif",
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <i className="ti ti-school" style={{ fontSize: 14 }} />
                    <span>{classe.nom}</span>
                  </button>
                ))}
              </div>
            </div>

            {currentClasse && (
              <>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    padding: 20,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    marginBottom: 20,
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: "0.05s",
                  }}
                >
                  <h4 style={{ color: "var(--pr)", marginBottom: 20, fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: "bold" }}>
                    <i className="ti ti-settings" style={{ marginRight: 8 }} />
                    Configuration du pointage
                  </h4>

                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div>
                      <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                        <i className="ti ti-login" style={{ marginRight: 8 }} />
                        Type de pointage
                      </label>
                      <select
                        value={typePointage}
                        onChange={(e) => {
                          setTypePointage(e.target.value);
                          setMotifSortie('');
                        }}
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: 12,
                          border: "1px solid #e5e7eb",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 14,
                          background: "#f9fafb",
                        }}
                      >
                        <option value="entree">Entrée</option>
                        <option value="sortie">Sortie</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                        <i className="ti ti-calendar" style={{ marginRight: 8 }} />
                        Date
                      </label>
                      <input
                        type="date"
                        value={datePointage}
                        onChange={(e) => setDatePointage(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: 12,
                          border: "1px solid #e5e7eb",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 14,
                          background: "#f9fafb",
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                        <i className="ti ti-clock" style={{ marginRight: 8 }} />
                        Heure
                      </label>
                      <input
                        type="time"
                        value={heurePointage}
                        onChange={(e) => setHeurePointage(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: 12,
                          border: "1px solid #e5e7eb",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 14,
                          background: "#f9fafb",
                        }}
                        step="1"
                      />
                    </div>

                    {typePointage === 'sortie' && checkMotifRequired() && (
                      <div>
                        <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif", color: "#d9534f" }}>
                          <i className="ti ti-alert-triangle" style={{ marginRight: 8 }} />
                          Motif de sortie *
                        </label>
                        <textarea
                          value={motifSortie}
                          onChange={(e) => setMotifSortie(e.target.value)}
                          placeholder="Motif requis pour cette sortie hors période normale..."
                          style={{
                            width: "100%",
                            padding: "12px",
                            borderRadius: 12,
                            border: "1px solid #d9534f",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14,
                            minHeight: 80,
                            resize: "vertical",
                            background: "#f9fafb",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "#fff",
                    padding: "10px 15px",
                    borderRadius: 30,
                    border: "1px solid #e5e7eb",
                    marginBottom: 20,
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: "0.1s",
                  }}
                >
                  <i className="ti ti-search" style={{ color: "var(--pr)" }} />
                  <input
                    type="text"
                    placeholder="Rechercher nom, prénom, matricule..."
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 14,
                      background: "transparent",
                    }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    marginBottom: 20,
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: "0.15s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "16px",
                      background: "#f8f9fa",
                      borderBottom: "2px solid var(--pr)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="ti ti-user-check" style={{ color: "var(--pr)" }} />
                      <span style={{ fontWeight: 700, color: "var(--pr)", fontFamily: "Inter, sans-serif", fontSize: 14 }}>
                        {selectedEleves.length} / {elevesFiltres.length} élève(s)
                      </span>
                    </div>

                    {elevesFiltres.length > 0 && (
                      <button
                        onClick={toggleSelectAll}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 12px",
                          background: "var(--pr)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 20,
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        <i className={`ti ${allSelected ? 'ti-check-box' : 'ti-box'}`} style={{ fontSize: 12 }} />
                        {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 400, overflowY: "auto" }}>
                    {loading ? (
                      <div style={{ textAlign: "center", padding: 40 }}>
                        <div className="loader-spinner" style={{ margin: "0 auto" }} />
                      </div>
                    ) : elevesFiltres.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                        <i className="ti ti-user" style={{ fontSize: 32, marginBottom: 10 }} />
                        <div>Aucun élève trouvé</div>
                      </div>
                    ) : (
                      elevesFiltres.map((eleve) => {
                        const isSelected = selectedEleves.includes(eleve.matricule);
                        return (
                          <div
                            key={eleve.matricule}
                            onClick={() => toggleEleveSelection(eleve.matricule)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "12px 16px",
                              borderBottom: "1px solid #f0f0f0",
                              cursor: "pointer",
                              background: isSelected ? "var(--pr-bg)" : "#fff",
                              transition: "all 0.2s",
                            }}
                          >
                            <div style={{ marginRight: 12 }}>
                              <i className={`ti ${isSelected ? 'ti-check-box' : 'ti-box'}`}
                                 style={{ color: isSelected ? '#28a745' : '#9ca3af', fontSize: 20 }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontFamily: "Inter, sans-serif", fontSize: 14 }}>
                                {eleve.prenom} {eleve.nom}
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Inter, sans-serif" }}>
                                Matricule: {eleve.matricule}
                              </div>
                            </div>
                            <div>
                              <span style={{
                                padding: "4px 8px",
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                background: eleve.genre === 'M' ? '#e3f2fd' : '#fce4ec',
                                color: eleve.genre === 'M' ? '#1976d2' : '#c2185b',
                              }}>
                                {eleve.genre === 'M' ? 'M' : 'F'}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleSubmitPointage(false)}
                  disabled={isPointageButtonDisabled}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background: isPointageButtonDisabled ? "#9ca3af" : "#28a745",
                    border: "none",
                    borderRadius: 30,
                    color: "#fff",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: "bold",
                    fontSize: 16,
                    cursor: isPointageButtonDisabled ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    transition: "all 0.2s",
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: "0.2s",
                  }}
                >
                  {saving ? (
                    <><div className="loader-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> Pointage en cours...</>
                  ) : (
                    <><i className="ti ti-device-floppy" /> Pointer {selectedEleves.length} élève(s)</>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {showMotifModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 24px",
        }}>
          <div style={{
            background: "#fff", borderRadius: 20,
            padding: "24px", width: "100%", maxWidth: 340,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, color: "#d9534f" }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24 }} />
              <h3 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontSize: 18, fontWeight: "bold" }}>Motif requis</h3>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                Motif de la sortie *
              </label>
              <textarea
                value={motifSortie}
                onChange={(e) => setMotifSortie(e.target.value)}
                placeholder="Décrivez le motif de la sortie..."
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  minHeight: 100,
                  resize: "vertical",
                }}
                autoFocus
              />
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowMotifModal(false)}
                style={{
                  padding: "10px 20px",
                  borderRadius: 30,
                  border: "1px solid #ddd",
                  background: "none",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  color: "#555",
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              <button
                onClick={validerMotif}
                style={{
                  padding: "10px 20px",
                  borderRadius: 30,
                  border: "none",
                  background: "#28a745",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                <i className="ti ti-device-floppy" style={{ marginRight: 8 }} />
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}